import Head from 'next/head'
import NextLink from 'next/link'
import * as Yup from 'yup'
import {
  Box,
  Card,
  Container,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Layout as AuthLayout } from 'layouts/Auth/layout'
import { useUserContext } from 'hooks'
import { register } from 'api/requests/auth'
import { UserRoles } from 'types/enums'
import { TokenStorage } from 'utils/auth'
import { getCurrentUserInfo } from 'api/requests/user'
import { useEffect, useState } from 'react'
import { NextPageWithLayout } from 'next'
import { emailShape, profileShape } from 'utils/auth/Validation'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { snakeCase } from 'lodash'
import { CheckboxCustom } from 'components/Checkbox'
import { LinkedText } from 'components/LinkedText'
import { Logo } from 'components/Logo'
import { useRouter } from 'next/router'
import { CheckCircle, Lock, SyncAlt } from '@mui/icons-material'
import { LogoIcon } from 'components/LogoIcon'
import { setCookie } from 'utils/cookie'
import { USER } from 'constants/cookieKeys'
import { LoadingButton } from '@mui/lab'
import { toast } from 'react-toastify'
import { HintsBlock } from 'components/HintsBlock'

interface IFormInput {
  password: string
  acceptTerms: boolean
}

interface EmailInput {
  email: string
}

const emailSchema = Yup.object().shape(emailShape)
const validationSchema = Yup.object().shape(profileShape)

const Register: NextPageWithLayout = () => {
  const { setUser, user } = useUserContext()
  const router = useRouter()
  const query = router.query

  const [stripeAccountId, setStripeAccountId] = useState('')
  const [timezone, setTimezone] = useState('')

  useEffect(() => {
    if (query.account_id) {
      const accountId = query?.account_id as string
      setStripeAccountId(accountId)
    }
  }, [query])

  const { control, handleSubmit, formState, getValues, watch } =
    useForm<IFormInput>({
      mode: 'all',
      defaultValues: {
        password:
          process.env.NEXT_PUBLIC_RUN_ENVIRONMENT != 'production'
            ? 'Secretpassword1'
            : '',
        acceptTerms: process.env.NEXT_PUBLIC_RUN_ENVIRONMENT != 'production',
      },
      resolver: yupResolver(validationSchema),
    })

  const watchPassword = watch('password')

  const {
    control: emailControl,
    formState: emailFormState,
    getValues: emailGetValues,
    trigger: emailTriggerValidation,
  } = useForm<EmailInput>({
    mode: 'all',
    defaultValues: {
      email:
        process.env.NEXT_PUBLIC_RUN_ENVIRONMENT != 'production'
          ? `email${Math.floor(Math.random() * 90 + 10)}@${Math.floor(
              Math.random() * 90 + 10
            )}.com`
          : '',
    },
    resolver: yupResolver(emailSchema),
  })

  const onSubmit = async () => {
    const { password } = getValues()
    const { email } = emailGetValues()

    try {
      const response = await register({
        password,
        email,
        role: UserRoles.Client,
        timezone,
      })

      if (!response || !response.data) {
        return
      }

      const { accessToken, refreshToken } = response.data
      const tokenStorage = TokenStorage.getInstance()
      tokenStorage.setAccessToken(accessToken)
      tokenStorage.setRefreshToken(refreshToken)

      const result = await getCurrentUserInfo()
      const user = result.data
      if (!user) {
        return
      }

      setCookie(USER, JSON.stringify(user))
      setUser(user)

      if (stripeAccountId !== '') {
        void router.push('/stripe-auth?account_id=' + stripeAccountId)
      } else {
        void router.push('/agent')
      }
    } catch (error) {
      toast.error('Failed to register')
    }
  }

  useEffect(() => {
    if (formState.isValid) {
      void emailTriggerValidation()
    }
  }, [formState.isValid])

  useEffect(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    setTimezone(timezone)
  }, [])

  return (
    <>
      <Head>
        <title>Register</title>
        <meta name='description' content='Register'></meta>
        <meta name='robots' content='noindex, nofollow'></meta>
      </Head>
      <Box
        component='main'
        sx={{
          flex: '1 1 auto',
          flexDirection: 'column',
          alignItems: 'center',
          alignContent: 'center',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Logo></Logo>
        </Box>
        {/* {stripeAccountId && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                mr: 4,
                maxWidth: '400px',
              }}
            >
              <Box
                display={'flex'}
                flexDirection={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
                columnGap={'5px'}
                sx={{ mb: 2, maxWidth: '200px' }}
              >
                <Box>
                  <LogoIcon width='40px' height='40px' />
                </Box>
                <SyncAlt color='success' sx={{ fontSize: '40px' }}></SyncAlt>
                <Box>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 36 36'
                    width='48'
                    height='40'
                  >
                    <path
                      fill='#635BFF'
                      d='M18 36a18 18 0 1 0 0-36 18 18 0 0 0 0 36Z'
                    />
                    <path
                      fill='#fff'
                      fillRule='evenodd'
                      d='M16.6 14c0-.85.69-1.18 1.84-1.18 1.66 0 3.74.5 5.4 1.4V9.1c-1.8-.72-3.59-1-5.39-1-4.42 0-7.35 2.3-7.35 6.16 0 6 8.27 5.04 8.27 7.64 0 1-.88 1.32-2.09 1.32-1.8 0-4.11-.74-5.94-1.74v5.18c2.03.87 4.07 1.24 5.94 1.24 4.53 0 7.64-2.24 7.64-6.14-.04-6.48-8.32-5.33-8.32-7.77Z'
                      clipRule='evenodd'
                    />
                  </svg>
                </Box>
              </Box>
              <Box>
                <Typography variant='h6' color='text.primary' sx={{ mb: 2 }}>
                  Chat with your Stripe Data
                </Typography>
                <Typography color={'text.secondary'} variant={'body2'}>
                  Lorem Ipsum is simply dummy text of the printing and
                  typesetting industry. Lorem Ipsum has been the industry&apos;s
                  standard dummy text ever since the 1500s, when an unknown
                  printer took a galley of type and scrambled it to make a type
                  specimen book. It has survived not only five centuries.
                </Typography>
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  alignItems={'center'}
                  my={2}
                >
                  <Lock sx={{ fontSize: '14px', mr: 1 }}></Lock>
                  <Box>
                    <Typography sx={{ fontSize: '13px' }}>
                      We take your data seriously. Read our
                      <LinkedText
                        fontSize={'13px'}
                        to={'https://getinsightai.com/terms-of-service'}
                        isExternal
                      >
                        Privacy Policy
                      </LinkedText>{' '}
                      ,
                      <LinkedText
                        fontSize={'13px'}
                        to={'https://getinsightai.com/privacy-policy'}
                        isExternal
                      >
                        Data Processing Addendum
                      </LinkedText>{' '}
                      and
                      <LinkedText
                        fontSize={'13px'}
                        to={'https://getinsightai.com/privacy-policy'}
                        isExternal
                      >
                        Security Practices
                      </LinkedText>
                      .
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </>
        )} */}

        <Container maxWidth='xs'>
          {/* <Logo> </Logo> */}
          <Card sx={{ border: '1px solid #eee', minWidth: '400px' }}>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                p: 4,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                {stripeAccountId && (
                  <CheckCircle
                    color='success'
                    sx={{ fontSize: '48px' }}
                  ></CheckCircle>
                )}

                <Typography variant='h5' sx={{ mt: 2, textAlign: 'center' }}>
                  {stripeAccountId ? 'Almost there!' : 'Create account'}
                </Typography>
                {/* Create your account to complete Stripe integration */}
                {stripeAccountId ? (
                  <>
                    {' '}
                    <Typography
                      color='text.secondary'
                      variant='body2'
                      sx={{ mt: 1, textAlign: 'center' }}
                    >
                      Create your account to complete Stripe integration
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography
                      color='text.secondary'
                      variant='body2'
                      sx={{ mt: 1, textAlign: 'center' }}
                    >
                      Already have an account? &nbsp;
                      <NextLink href='/login'>Log in</NextLink>
                    </Typography>
                  </>
                )}
              </Box>
              <div>
                <Stack spacing={3}>
                  <Controller
                    name='email'
                    control={emailControl}
                    render={({ field, fieldState }) => (
                      <TextField
                        fullWidth
                        {...field}
                        type='email'
                        label='Email address'
                        id={snakeCase(field.name)}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message ?? ''}
                      />
                    )}
                  />
                  <Controller
                    name='password'
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        fullWidth
                        type='password'
                        {...field}
                        label='Create a password'
                        id={snakeCase(field.name)}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message ?? ''}
                      />
                    )}
                  />
                  <Stack spacing={2} sx={{ mt: 3 }}>
                    <HintsBlock password={watchPassword} />
                  </Stack>
                  <Controller
                    name='acceptTerms'
                    control={control}
                    render={({ field }) => (
                      <CheckboxCustom
                        {...field}
                        checked={field.value}
                        sx={{
                          textAlign: 'left',
                          mr: 0,
                          '.MuiLink-root': {
                            fontWeight: 600,
                            textDecoration: 'none',
                            padding: '0 5px',
                          },
                        }}
                        label={
                          <Typography sx={{ fontSize: '11px' }}>
                            I&apos;ve agreed with the
                            <LinkedText
                              fontSize={'11px'}
                              to={'https://getinsightai.com/terms-of-service'}
                              isExternal
                            >
                              Terms of Service
                            </LinkedText>{' '}
                            and
                            <LinkedText
                              fontSize={'11px'}
                              to={'https://getinsightai.com/privacy-policy'}
                              isExternal
                            >
                              Privacy Policy
                            </LinkedText>
                            .
                          </Typography>
                        }
                      />
                    )}
                  />
                </Stack>
                <Box>
                  <LoadingButton
                    fullWidth
                    size='large'
                    sx={{ mt: 3 }}
                    type='submit'
                    variant='contained'
                    loading={formState.isSubmitting}
                    disabled={formState.isSubmitting || !formState.isValid}
                    onClick={handleSubmit(onSubmit)}
                  >
                    Continue
                  </LoadingButton>
                </Box>
              </div>
            </Box>
          </Card>

          {!stripeAccountId && (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
                py: 3,
              }}
            >
              {/* <Logo></Logo> */}
            </Box>
          )}
        </Container>
      </Box>
    </>
  )
}

Register.getLayout = (page: any) => <AuthLayout>{page}</AuthLayout>

export default Register
